import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useVault } from "@/context/VaultContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { UnlockScreen } from "@/components/passord/UnlockScreen";
import { EntryRow } from "@/components/passord/EntryRow";
import { PasswordField } from "@/components/passord/PasswordField";
import { TotpCode } from "@/components/passord/TotpCode";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { normalizeUrl } from "@/lib/vaultCrypto";
import { formatDate } from "@/lib/format";
import { Share2, Search, ExternalLink, Eye } from "lucide-react";

const ReadonlyField = ({ label, value, testId }) => (
  <div className="space-y-1.5"><Label>{label}</Label><Input value={value || "–"} readOnly className="bg-secondary/60" data-testid={testId} /></div>
);

export default function SharedPage() {
  const { status, shared, settings } = useVault();
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return shared.filter((e) => !s || [e.title, e.username, e.url, e.owner?.full_name].some((v) => (v || "").toLowerCase().includes(s))).sort((a, b) => (a.title || "").localeCompare(b.title || "", "nb"));
  }, [shared, q]);

  if (status === "loading") return <Skeleton className="h-72" />;
  if (status === "none") return <EmptyState icon={Share2} title="Sett opp hvelvet først" description="Delte passord dekrypteres med din private nøkkel, som opprettes når du setter opp hvelvet." actionLabel="Til hvelvet" actionTo="/passord" />;
  if (status === "locked") return <div className="space-y-6"><PageHeader eyebrow="Passord" title="Delt med meg" /><UnlockScreen description="Lås opp for å se passord som er delt med deg." /></div>;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Passord" title="Delt med meg" description="Passord familie eller fullmektig har delt med deg. Kun lesing – eieren styrer innholdet." />
      {shared.length === 0 ? (
        <EmptyState icon={Share2} title="Ingenting er delt med deg ennå" description="Personer du har fullmakt-relasjon til kan dele enkeltpassord med deg fra sitt hvelv." actionLabel="Familie og fullmakt" actionTo="/familie" testId="shared-empty-state" />
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk …" className="pl-9" aria-label="Søk i delte" data-testid="shared-search-input" />
          </div>
          <ul className="space-y-2" data-testid="shared-entry-list">
            {filtered.map((e) => <EntryRow key={e.id} entry={{ ...e, shares: [] }} settings={settings} readonly ownerLabel={e.owner?.full_name} onEdit={setView} />)}
          </ul>
        </>
      )}

      <Sheet open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <SheetContent className="w-full sm:max-w-lg bg-card overflow-y-auto" data-testid="shared-view-sheet">
          {view ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">{view.title} <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-normal text-muted-foreground"><Eye className="h-3 w-3" /> Kun lesing</span></SheetTitle>
                <SheetDescription>Delt av {view.owner?.full_name} · oppdatert {formatDate(view.updated_at)}</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <ReadonlyField label="Nettadresse" value={view.url} testId="shared-url" />
                <ReadonlyField label="Brukernavn" value={view.username} testId="shared-username" />
                <div className="space-y-1.5"><Label>Passord</Label><PasswordField value={view.password} readOnly testId="shared-password" clearSeconds={settings.clipboard_clear_seconds} /></div>
                {view.totp ? <div className="space-y-1.5"><Label>Engangskode (2FA)</Label><div><TotpCode secret={view.totp} testId="shared-totp" /></div></div> : null}
                {view.notes ? <div className="space-y-1.5"><Label>Notater</Label><p className="rounded-lg border bg-secondary/60 p-3 text-sm whitespace-pre-wrap" data-testid="shared-notes">{view.notes}</p></div> : null}
                {view.url ? <Button asChild className="w-full" data-testid="shared-goto"><a href={normalizeUrl(view.url)} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-2" /> Gå til {view.title}</a></Button> : null}
                <p className="text-xs text-muted-foreground">Vil du ha egne kopier? <Link to="/passord" className="underline">Legg det inn i ditt eget hvelv</Link>.</p>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
